import {
  ArrowsClockwise,
  CalendarSlash,
  CaretLeft,
  Clock,
  CreditCard,
  FileArrowDownIcon,
  Hash,
  IdentificationCard,
  Sparkle,
  Tag,
  Warning,
  XCircle
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { Badge, Button, Spinner } from 'flowbite-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table } from '../../components/Table';
import { useSubscriptionHistory } from '../../hooks';
import { ISubscriptionHistoryItem } from '../../interfaces';
import { getCurrencySymbol } from '../../lib/utils';


import { BillingHistorySkeleton } from '../../components/SkeletonLoaders';
import { CurrencyCode } from '../../shared/constants';


const DownloadInvoiceButton = ({ url, invoiceId }: { url: string; invoiceId: string }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation();

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDownloading(true);
    try {
      // Attempt programmatic download to local machine
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `invoice_${invoiceId || 'bill'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn('Programmatic download failed (CORS or network), falling back to new tab:', error);
      // Fallback: Open in new tab if CORS or other issues occur
      window.open(url, '_blank', 'noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      size="xs"
      color="light"
      onClick={handleDownload}
      // disabled={isDownloading}
      isProcessing={isDownloading}
      processingSpinner={<Spinner size="sm" color="info" />}
      className="group rounded-xl border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all font-bold shadow-sm h-[34px]"
    >
      <div className="flex items-center gap-2">
        {!isDownloading && (
          <FileArrowDownIcon size={16} weight="bold" className="text-indigo-600 group-hover:scale-110 transition-transform" />
        )}
        <span className="text-[11px] text-gray-700 group-hover:text-indigo-700 whitespace-nowrap uppercase tracking-tight">
          {t('billing.downloadInvoice', 'Download Invoice')}
        </span>
      </div>
    </Button>
  );
};

const BillingHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: historyData, isLoading: isLoadingHistory } = useSubscriptionHistory();
  const history = historyData?.results || [];

  const columns: ColumnDef<ISubscriptionHistoryItem>[] = [
    // ... rest of the column definitions
    {
      accessorKey: 'event_date',
      size: 100,
      header: t('billing.history.date', 'Date'),
      cell: (info) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900">
            {new Date(info.getValue() as string).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <span className="text-[10px] text-gray-400 font-medium lowercase">
            {new Date(info.getValue() as string).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      size: 100,
      header: t('billing.history.amount', 'Amount'),
      cell: (info) => {
        const row = info.row.original;
        if (!info.getValue()) return <span className="text-gray-300">—</span>;
        const symbol = getCurrencySymbol(row.currency as CurrencyCode);
        return (
          <span className="font-extrabold text-gray-900 text-sm">
            {symbol}{parseFloat(info.getValue() as string).toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: 'event_type_display',
      header: t('billing.history.event', 'Event'),
      size: 150,
      cell: (info) => {
        const row = info.row.original;
        const isSuccess = row.status === 'success' || row.status === 'active';
        const metadataStatus = row.metadata?.status;

        return (
          <div className="flex flex-col gap-1 min-w-[120px]">
            <span className="text-xs font-extrabold text-gray-800 uppercase tracking-tight">{info.getValue() as string}</span>
            <div className="flex flex-wrap gap-1">
              <Badge
                color={isSuccess ? 'success' : (row.status === 'failed' ? 'failure' : 'warning')}
                size="xs"
                className="w-fit px-1.5 py-0 rounded-md font-bold uppercase text-[9px]"
              >
                {row.status_display}
              </Badge>
              {metadataStatus === 'active' && (
                <Badge
                  color="success"
                  size="xs"
                  className="w-fit px-1.5 py-0 rounded-md font-bold uppercase text-[9px] bg-green-50 text-green-700 border border-green-100 shadow-sm"
                >
                  {t('billing.status.active', 'Status: Active')}
                </Badge>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'description',
      minSize: 200,
      header: t('billing.history.activity', 'Activity'),
      cell: (info) => {
        const row = info.row.original;
        const eventType = row.event_type;
        const description = info.getValue() as string;

        let Icon = Clock;
        let iconColor = 'bg-gray-100 text-gray-500';
        let title = t('billing.event.generic', 'Activity Recorded');

        // Logic to determine icon and title based on event type or description keywords
        if (eventType.includes('payment_succeeded') || description.toLowerCase().includes('payment') && description.toLowerCase().includes('succeeded')) {
          Icon = CreditCard;
          iconColor = 'bg-emerald-100 text-emerald-600';
          title = t('billing.event.payment_success', 'Payment Successful');
        } else if (eventType.includes('payment_failed') || description.toLowerCase().includes('payment failed')) {
          Icon = Warning;
          iconColor = 'bg-red-100 text-red-600';
          title = t('billing.event.payment_failed', 'Payment Failed');
        } else if (description.toLowerCase().includes('cancelled') || description.toLowerCase().includes('canceled')) {
          Icon = XCircle;
          iconColor = 'bg-rose-100 text-rose-600';
          title = t('billing.event.cancelled', 'Subscription Cancelled');
        } else if (description.toLowerCase().includes('scheduled for cancellation')) {
          Icon = CalendarSlash;
          iconColor = 'bg-orange-100 text-orange-600';
          title = t('billing.event.scheduled_cancel', 'Cancellation Scheduled');
        } else if (eventType.includes('subscription_updated') || description.toLowerCase().includes('updated')) {
          Icon = ArrowsClockwise;
          iconColor = 'bg-indigo-100 text-indigo-600';
          title = t('billing.event.updated', 'Subscription Updated');
        } else if (eventType.includes('subscription_created') || description.toLowerCase().includes('created')) {
          Icon = Sparkle;
          iconColor = 'bg-teal-100 text-teal-600';
          title = t('billing.event.created', 'Subscription Started');
        }

        return (
          <div className="flex items-start gap-3 py-1 min-w-[240px] max-w-[320px]">
            <div className={`p-2 rounded-xl shrink-0 border border-white shadow-sm ${iconColor}`}>
              <Icon size={18} weight="duotone" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-bold text-gray-900 leading-none mb-1">{title}</span>
              <p className="text-[11px] text-gray-400 font-medium leading-tight line-clamp-2">
                {description}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'plan_name',
      header: t('billing.history.plan', 'Plan'),
      cell: (info) => (
        <div className="flex items-center gap-1.5">
          <Tag size={12} className="text-indigo-400" />
          <span className="text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full whitespace-nowrap">
            {info.getValue() as string || '-'}
          </span>
        </div>
      ),
    },
    {
      id: 'references',
      header: t('billing.history.reference', 'Reference'),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col gap-1 min-w-[100px]">
            {row.stripe_invoice_id && (
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <Hash size={10} className="shrink-0" />
                <span className="font-mono truncate max-w-[80px]" title={row.stripe_invoice_id}>INV: {row.stripe_invoice_id}</span>
              </div>
            )}
            {row.stripe_subscription_id && (
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <IdentificationCard size={10} className="shrink-0" />
                <span className="font-mono truncate max-w-[80px]" title={row.stripe_subscription_id}>SUB: {row.stripe_subscription_id}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => {
        const row = info.row.original;
        const pdf = row.metadata?.invoice_pdf;
        if (!pdf) return null;
        return (
          <div className="flex justify-end pr-2">
            <DownloadInvoiceButton url={pdf} invoiceId={row.stripe_invoice_id || ''} />
          </div>
        );
      },
    },
  ];

  if (isLoadingHistory) {
    return <BillingHistorySkeleton />;
  }

  return (
    <div className='space-y-2'>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2">
          <Button
            color="light"
            size="xs"
            onClick={() => navigate('/billing')}
            className="shadow-sm h-8 w-8 flex items-center justify-center"
          >
            <CaretLeft size={18} weight="bold" />
          </Button>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-700">
              {t('billing.paymentHistory', 'Payment History')}
            </h1>
          </div>
        </div>
      </header>

      <Table
        data={history}
        columns={columns}
        pageSize={20}
        maxHeight="calc(100vh - 200px)"
        className="bg-transparent"
      />
    </div>
  );
};

export default BillingHistory;
